import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonTitle,
  IonToolbar,
  IonGrid,
  IonItemDivider,
  IonLabel,
  IonSpinner,
  IonList,
  IonRow,
  IonCol,
  IonItemSliding,
  IonCard,
  IonCardContent,
  IonItemOptions,
  IonItemOption,
  IonItem,
  IonToggle,
} from '@ionic/react'
import React, { BaseSyntheticEvent, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { addCircleOutline, chevronBackOutline, trash, trashOutline } from 'ionicons/icons'
import { TabConstants } from '../../../../components/tabs/Tabs.constants'
import PCRHeader from '../../../../components/pcrHeader/PCRHeader.component'
import {
  setDeleteProcedure,
  setProcedureLoaderItem,
  syncPtaWithParentVitals,
} from '../../../timeline/store/timeline.actions'
import { TimelineConstants } from '../../../timeline/store/timeline.constants'
import TimesRow from '../../../times/timesRow/TimesRow.component'
import './pnMedication.page.css'
import { getPatientTimelineSelector } from '../../../timeline/selectors/patientTimeline.selector'
import SingleSelect from '../../../../components/singleSelect/SingleSelect.component'
import { getPnMedicationSelector } from '../selectors/pnMedication.selector'
import { PnMedicationConstants } from '../store/pnMedication.constants'
import { IAddPnMedication } from '../store/pnMedication.types'
import NumberPad from '../../../../components/numberPad/NumberPad.component'
import Validation from '../../../../components/validation/Validation.component'
import { showAlert } from '../../../../components/alert/store/alert.actions'
import { IOption, IPNNVOption } from '../../../../common/common.types'
import {
  addPnMedication,
  deletePnMedication,
  setMedDose,
  setMedDoseUnit,
  setMedDoseUnitPNNV,
  setMedRoute,
  setMedRoutePNNV,
  setMedication,
  setMedicationPNNV,
  savePnMedication,
  setPTA,
} from '../store/pnMedication.actions'
import {
  getAddMedicationAdministeredOptionsSelector,
  getAllPNNVOptionsSelector,
  getPnMedicationsOptionsSelector,
} from '../../../options/selectors'
import { compareObjectsIfEqual, filterOutObjectKeys } from '../../../../utilities/object.util'
import { initialPnMedicationState } from '../store/pnMedication.reducers'
import { numberPadKeysWithDecimal } from '../../../../components/numberPad/NumberPad.types'
import { getTimesSelector } from '../../../times/selectors/getTimes.selector'
import { isValidZollNowDate, isValidZollTime } from '../../../../utilities/dateTime.util'
import { getPTADateTimeErrorMessage, isProcDateTimePTA } from '../../../../utilities/procedures.util'
import { IServiceConfig } from '../../../configurations/store/configurations.types'
import { getServiceConfigSelector } from '../../../configurations/selectors/serviceConfig.selector'
import { goBack } from '../../../../utilities/navigation.util'
import { IAlertState } from '../../../../components/alert/store/alert.types'
import { getCurrentDate, getCurrentTime } from '../../../../utilities'

const PnMedication: React.FC = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const history = useHistory()

  const pnMedicationState = useSelector(getPnMedicationSelector)
  const { time, date, pta, addMedicationAdministeredData } = pnMedicationState
  const timelineState = useSelector(getPatientTimelineSelector)
  const { procedureLoader } = timelineState
  const pnMedicationOptionsState = useSelector(getPnMedicationsOptionsSelector)
  const { medicationOptions } = pnMedicationOptionsState
  const allPNNVOptions = useSelector(getAllPNNVOptionsSelector)
  const medicationPNNVOptions = allPNNVOptions?.['eMedications.03']
  const { doseUnitOptions, routeOptions } = useSelector(getAddMedicationAdministeredOptionsSelector)
  const { atPatientDate, atPatientTime, onSceneDate, onSceneTime } = useSelector(getTimesSelector)

  const { DISPLAY_SECONDS }: IServiceConfig = useSelector(getServiceConfigSelector)
  const displaySeconds = !!DISPLAY_SECONDS

  const runPTAValidation = () => {
    const errMsg = getPTADateTimeErrorMessage(
      atPatientDate,
      atPatientTime,
      onSceneDate,
      onSceneTime,
      date,
      time,
      displaySeconds
    )
    if (errMsg) {
      dispatch(showAlert({ message: errMsg }))
      dispatch(setPTA(false))
      dispatch(syncPtaWithParentVitals(false))
    }
  }

  useEffect(() => {
    if (pta && isValidZollNowDate(date)) {
      runPTAValidation()
    } else if (!pta && isValidZollNowDate(date) && isValidZollTime(time)) {
      if (isProcDateTimePTA(atPatientDate, atPatientTime, onSceneDate, onSceneTime, date, time, displaySeconds)) {
        dispatch(setPTA(true))
        dispatch(syncPtaWithParentVitals(true))
      }
    }
  }, [date, time, pta])

  const dateTimeValidationCheck = () => {
    if (!date || !time) {
      dispatch(showAlert({ header: '', message: 'Date / Time is required' }))
      return false
    }
    return true
  }

  const save = () => {
    dispatch(savePnMedication())
  }

  const initialStateCheck = () => {
    if (pta && ((date && time) || (!date && !time))) return true
    const fieldsToFilter = ['key', 'addToTimeline', 'updateInputRows']
    const currentState = filterOutObjectKeys(pnMedicationState, fieldsToFilter)
    const initalState = filterOutObjectKeys(initialPnMedicationState, fieldsToFilter)
    if (!compareObjectsIfEqual(currentState, initalState)) {
      return dateTimeValidationCheck()
    }
    return true
  }

  const medicationValidate = (type: string) => {
    let hasInvalidData = false
    addMedicationAdministeredData?.forEach((medValue) => {
      if (medValue?.medication === null) {
        dispatch(showAlert({ header: '', message: 'Medication is required' }))
        hasInvalidData = true
        return
      }
      if (medValue?.medicationPNNV === null) {
        dispatch(showAlert({ header: '', message: 'Value is required' }))
        hasInvalidData = true
        return
      }
    })
    if (!hasInvalidData) {
      if (type === 'save') {
        history.push(TabConstants.TIMELINE_TAB)
      }
      if (type === 'back') {
        goBack()
      }
    }
  }

  const onPTAClick = () => {
    const isPTA = !pta
    dispatch(setPTA(isPTA))
    dispatch(syncPtaWithParentVitals(isPTA))
  }

  const onSaveClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if (!pta && (!date || !time)) {
      dateTimeValidationCheck()
      return
    }
    const dateTimeValidationCheckResult = initialStateCheck()
    if (dateTimeValidationCheckResult) {
      medicationValidate('save')
    }
  }

  const onBackClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if ((!date || !time) && !pta) {
      const alert: IAlertState = {
        message: t('common.saveTimelineEntry'),
        buttons: [
          {
            text: t('common.buttons.Yes'),
            handler: () => {
              const date = getCurrentDate()
              let time = getCurrentTime()
              if (!displaySeconds) {
                time = time.replace(/(:\d+)(:\d+)$/, '$1:00')
              }

              dispatch({ type: 'pnMedication/SET_TIME', time: time })
              dispatch({ type: 'pnMedication/SET_DATE', date: date })

              dispatch(savePnMedication())

              goBack()
            },
          },
          {
            text: t('common.buttons.No'),
            role: 'destructive',
            handler: () => {
              const itemToRemove = {
                key: pnMedicationState.key,
                rowActionType: TimelineConstants.SET_PN_MEDICATION_ITEM,
              }
              dispatch(setDeleteProcedure(true))
              dispatch({ type: TimelineConstants.DELETE_TIMELINE_ITEM, payload: itemToRemove })
              goBack()
            },
          },
        ],
      }
      dispatch(showAlert(alert))
      dispatch(setProcedureLoaderItem(false))
    } else {
      const dateTimeValidationCheck = initialStateCheck()
      if (dateTimeValidationCheck) {
        medicationValidate('back')
      }
      dispatch(setProcedureLoaderItem(false))
    }
  }

  const setDoseOnChange = (values: string[], medication: IAddPnMedication) => {
    dispatch(setMedDose(values.length ? values.join('') : null, medication))
    save()
  }

  const setDoseUnitsOnChange = (option: IOption, medication: IAddPnMedication) => {
    dispatch(setMedDoseUnit(option, medication))
    dispatch(setMedDoseUnitPNNV(null, medication))
    save()
  }

  const setDoseUnitsPNNVOnChange = (option: IPNNVOption | null, medication: IAddPnMedication) => {
    dispatch(setMedDoseUnitPNNV(option, medication))
    dispatch(setMedDoseUnit(null, medication))
    save()
  }

  const setMedicationPNNVOnChange = (option: IPNNVOption | null, medication: IAddPnMedication) => {
    medication.medicationPNNV = option
    dispatch(setMedicationPNNV(option, medication))
    save()
  }

  const setMedicationOnChange = (option: IOption, medication: IAddPnMedication) => {
    dispatch(setMedication(option, medication))
    save()
  }

  const setRouteOnChange = (option: IOption, medication: IAddPnMedication) => {
    dispatch(setMedRoute(option, medication))
    dispatch(setMedRoutePNNV(null, medication))
    save()
  }

  const setRoutePNNVOnChange = (option: IPNNVOption | null, medication: IAddPnMedication) => {
    dispatch(setMedRoutePNNV(option, medication))
    dispatch(setMedRoute(null, medication))
    save()
  }

  const onDeletePnMedication = (medication: IAddPnMedication) => {
    dispatch(deletePnMedication(medication))
    save()
  }

  const onAddPnMedication = () => {
    dispatch(addPnMedication())
    save()
  }

  return (
    <IonPage id='PNMedicationPage'>
      <PCRHeader />
      <IonHeader>
        <IonToolbar color='light'>
          <IonButtons slot='start'>
            <IonButton data-testid='Back' fill='clear' onClick={onBackClick} color='primary-alt'>
              <IonIcon icon={chevronBackOutline} />
              {t('common.buttons.Back')}
            </IonButton>
          </IonButtons>
          <IonButtons slot='end'>
            <IonButton data-testid='Save' fill='clear' onClick={onSaveClick} color='primary-alt'>
              {t('common.buttons.Save')}
            </IonButton>
          </IonButtons>
          <IonTitle>{t('pnMedication.PNMedication')}</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen color={'light'}>
        {procedureLoader ? (
          <IonSpinner className='procedure-loader' data-testid='Spinner' />
        ) : (
          <IonList>
            <IonItemDivider color='light' className='ion-padding'>
              <IonLabel className='grid-item-divider-label' color='medium'>
                {t('pnMedication.addPnMedications')}
              </IonLabel>
            </IonItemDivider>

            <IonRow>
              <IonCol className='picklist'>
                <TimesRow
                  elementId='Date-Time'
                  header={t('pnMedication.dateTime')}
                  time={time}
                  date={date}
                  timeAction={PnMedicationConstants.SET_TIME}
                  dateAction={PnMedicationConstants.SET_DATE}
                  currentDateTimeAction={PnMedicationConstants.SET_DATE_TIME}
                  subHeader={''}
                />
              </IonCol>
            </IonRow>

            <hr className='field-divider' />

            <IonRow>
              <IonCol>
                <IonItem lines='none'>
                  <IonLabel>{t('procedure.header.pta')}</IonLabel>
                  <IonToggle
                    data-testid='PTA'
                    slot='start'
                    name='pta'
                    checked={pta}
                    onClick={onPTAClick}
                    color='zoll-blue-secondary'
                  />
                </IonItem>
              </IonCol>
            </IonRow>

            <IonItemDivider color='light' />
            <IonList id='addMedicationAdministeredData' className='list-background'>
              {addMedicationAdministeredData?.map((medication: IAddPnMedication, index: number) => (
                <IonItemSliding key={medication?.id.toString()}>
                  <IonCard
                    id={medication?.id.toString()}
                    className='ion-no-margin ion-margin-vertical'
                    data-testid='AddPnMedication'>
                    <IonCardContent>
                      <IonGrid className='no-padding'>
                        <IonRow>
                          <IonCol>
                            {t('pnMedication.medication')} {index + 1}
                          </IonCol>
                          <IonButtons>
                            <IonButton
                              data-testid={`deleteMedicationAdministered${index}`}
                              onClick={() => onDeletePnMedication(medication)}>
                              <IonIcon icon={trashOutline} />
                            </IonButton>
                          </IonButtons>
                        </IonRow>
                        <IonRow>
                          <IonCol className='picklist'>
                            <SingleSelect
                              dataTestID={`Value${index}`}
                              options={medicationPNNVOptions}
                              selectedPNNVOption={medication.medicationPNNV}
                              listHeader={t('pnMedication.value')}
                              label={t('pnMedication.value')}
                              action={() => {
                                /**/
                              }}
                              pnnvAction={setMedicationPNNVOnChange}
                              showPNNVAsNormals={true}
                              dispatchData={medication}
                            />
                          </IonCol>
                        </IonRow>

                        <hr className='field-divider' />

                        <IonRow>
                          <IonCol className='picklist'>
                            <SingleSelect
                              dataTestID={`Medication${index}`}
                              options={medicationOptions}
                              selectedOption={medication?.medication}
                              listHeader={t('pnMedication.medication')}
                              label={t('pnMedication.medication')}
                              action={setMedicationOnChange}
                              dispatchData={medication}
                            />
                          </IonCol>
                        </IonRow>

                        <hr className='field-divider' />

                        <IonRow>
                          <IonCol className='picklist'>
                            <NumberPad
                              elementId={`Dose${index}`}
                              dataTestId={`Dose${index}`}
                              values={medication.dose ? medication.dose.toString().split('') : []}
                              label={t('pnMedication.dose')}
                              validCharacters={numberPadKeysWithDecimal}
                              onNumPadClick={(values) => {
                                setDoseOnChange(values, medication)
                              }}
                            />
                            <Validation id={PnMedicationConstants.SET_DOSE} cardinalityKey={medication.key} />
                          </IonCol>

                          <IonCol className='picklist'>
                            <SingleSelect
                              dataTestID={`DoseUnits${index}`}
                              options={doseUnitOptions}
                              selectedOption={medication.doseUnit}
                              listHeader={t('pnMedication.doseUnit')}
                              label={t('pnMedication.doseUnit')}
                              action={setDoseUnitsOnChange}
                              dispatchData={medication}
                              selectedPNNVOption={medication.doseUnitPNNV}
                              pnnvAction={setDoseUnitsPNNVOnChange}
                            />
                          </IonCol>
                        </IonRow>

                        <hr className='field-divider' />
                        <IonRow>
                          <IonCol className='picklist'>
                            <SingleSelect
                              label={t('pnMedication.route')}
                              listHeader='Route'
                              dataTestID={`Route${index}`}
                              options={routeOptions}
                              action={setRouteOnChange}
                              selectedOption={medication.route}
                              dispatchData={medication}
                              selectedPNNVOption={medication.routePNNV}
                              pnnvAction={setRoutePNNVOnChange}
                            />
                          </IonCol>
                        </IonRow>
                      </IonGrid>
                    </IonCardContent>
                  </IonCard>
                  <IonItemOptions side='end' className='delete-swipe'>
                    <IonItemOption
                      data-testid={`DeleteIcon${index}`}
                      color='danger'
                      onDrag={() => onDeletePnMedication(medication)}
                      onClick={() => onDeletePnMedication(medication)}>
                      <IonIcon slot='icon-only' icon={trash} />
                    </IonItemOption>
                  </IonItemOptions>
                </IonItemSliding>
              ))}
              <IonRow className='ion-float-right'>
                <IonButton
                  data-testid='AddPnMedication'
                  className='transform-none'
                  fill='clear'
                  color='primary-alt'
                  onClick={() => onAddPnMedication()}>
                  <IonIcon icon={addCircleOutline} />
                  <IonLabel>{t('pnMedication.addMedication')}</IonLabel>
                </IonButton>
              </IonRow>
            </IonList>

            <IonItemDivider color='light' />
          </IonList>
        )}
      </IonContent>
    </IonPage>
  )
}

export default PnMedication
